'use client';

import { useMemo, useState } from 'react';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const SAMPLE_XML = `<library>
  <book category="fiction">
    <title lang="en">The Great Escape</title>
    <author>Jane Doe</author>
    <price>12.99</price>
  </book>
  <book category="tech">
    <title lang="en">Learning XPath</title>
    <author>John Smith</author>
    <price>29.50</price>
  </book>
</library>`;

type XpathNode = {
  kind: string;
  serialized: string;
};

type EvalResult =
  | { status: 'empty' }
  | { status: 'error'; message: string }
  | { status: 'nodes'; nodes: XpathNode[] }
  | { status: 'value'; label: string; value: string };

function serializeNode(node: Node): XpathNode {
  if (node.nodeType === Node.ELEMENT_NODE) {
    return { kind: 'element', serialized: new XMLSerializer().serializeToString(node) };
  }
  if (node.nodeType === Node.ATTRIBUTE_NODE) {
    const attr = node as Attr;
    return { kind: 'attribute', serialized: `${attr.name}="${attr.value}"` };
  }
  if (node.nodeType === Node.TEXT_NODE || node.nodeType === Node.CDATA_SECTION_NODE) {
    return { kind: 'text', serialized: (node.nodeValue ?? '').trim() };
  }
  if (node.nodeType === Node.COMMENT_NODE) {
    return { kind: 'comment', serialized: `<!--${node.nodeValue ?? ''}-->` };
  }
  return { kind: 'node', serialized: node.nodeValue ?? node.nodeName };
}

function evaluate(xml: string, expression: string): EvalResult {
  if (!xml.trim() || !expression.trim()) {
    return { status: 'empty' };
  }

  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(xml, 'application/xml');
  } catch (error) {
    return {
      status: 'error',
      message: error instanceof Error ? error.message : 'The XML could not be parsed.',
    };
  }

  const parserError = doc.getElementsByTagName('parsererror')[0];
  if (parserError) {
    return {
      status: 'error',
      message: parserError.textContent?.trim() || 'The XML is not well-formed.',
    };
  }

  let evaluated: XPathResult;
  try {
    evaluated = doc.evaluate(expression, doc, null, XPathResult.ANY_TYPE, null);
  } catch (error) {
    return {
      status: 'error',
      message:
        error instanceof Error ? error.message : 'The XPath expression could not be evaluated.',
    };
  }

  switch (evaluated.resultType) {
    case XPathResult.NUMBER_TYPE:
      return { status: 'value', label: 'Number', value: String(evaluated.numberValue) };
    case XPathResult.STRING_TYPE:
      return { status: 'value', label: 'String', value: evaluated.stringValue };
    case XPathResult.BOOLEAN_TYPE:
      return { status: 'value', label: 'Boolean', value: String(evaluated.booleanValue) };
    default: {
      const nodes: XpathNode[] = [];
      let current = evaluated.iterateNext();
      let guard = 0;
      while (current && guard < 10_000) {
        nodes.push(serializeNode(current));
        current = evaluated.iterateNext();
        guard += 1;
      }
      return { status: 'nodes', nodes };
    }
  }
}

export default function BrowserXpathTester() {
  const [xml, setXml] = useState(SAMPLE_XML);
  const [expression, setExpression] = useState('//book[@category="tech"]/title');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const result = useMemo(() => evaluate(xml, expression), [xml, expression]);

  const matchCount =
    result.status === 'nodes' ? result.nodes.length : result.status === 'value' ? 1 : 0;

  async function handleCopy() {
    let text = '';
    if (result.status === 'nodes') {
      text = result.nodes.map((node) => node.serialized).join('\n');
    } else if (result.status === 'value') {
      text = result.value;
    }
    if (!text) {
      return;
    }
    try {
      await copyText(text);
      setCopyMessage('Result copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the result.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">XPath</span>
        <h2 className="tool-live-title">XPath tester</h2>
        <p className="tool-live-subtitle">
          Paste XML, write an XPath expression, and see the matched nodes serialized live. Parsing
          and evaluation happen entirely in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>XPath expression</span>
              <input
                onChange={(event) => setExpression(event.target.value)}
                placeholder="//book/title"
                spellCheck={false}
                type="text"
                value={expression}
              />
            </label>

            <label className="tool-live-field tool-live-field-full">
              <span>XML input</span>
              <textarea
                onChange={(event) => setXml(event.target.value)}
                placeholder="<root>…</root>"
                rows={14}
                spellCheck={false}
                value={xml}
              />
            </label>
          </div>

          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              disabled={matchCount === 0}
              onClick={handleCopy}
              type="button"
            >
              Copy result
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
          {result.status === 'error' ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {result.message}
            </p>
          ) : null}
        </section>

        <section className="tool-live-preview">
          <div className="tool-live-field tool-live-field-full">
            <span>
              Result
              {result.status === 'nodes' ? ` · ${result.nodes.length} node(s)` : ''}
            </span>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.6rem',
                minHeight: '6rem',
                maxHeight: '24rem',
                overflow: 'auto',
              }}
            >
              {result.status === 'empty' ? (
                <span style={{ opacity: 0.6 }}>
                  Enter XML and an XPath expression to see matches.
                </span>
              ) : null}

              {result.status === 'value' ? (
                <div
                  style={{
                    border: '1px solid rgba(148, 163, 184, 0.35)',
                    borderRadius: '0.5rem',
                    padding: '0.6rem 0.75rem',
                    fontFamily: 'var(--font-mono, monospace)',
                    fontSize: '0.85rem',
                  }}
                >
                  <strong>{result.label}: </strong>
                  {result.value}
                </div>
              ) : null}

              {result.status === 'nodes' && result.nodes.length === 0 ? (
                <span style={{ opacity: 0.6 }}>No nodes matched this expression.</span>
              ) : null}

              {result.status === 'nodes'
                ? result.nodes.map((node, index) => (
                    <div
                      key={index}
                      style={{
                        border: '1px solid rgba(148, 163, 184, 0.35)',
                        borderRadius: '0.5rem',
                        padding: '0.6rem 0.75rem',
                      }}
                    >
                      <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.3rem' }}>
                        #{index + 1} · {node.kind}
                      </div>
                      <pre
                        style={{
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontFamily: 'var(--font-mono, monospace)',
                          fontSize: '0.85rem',
                        }}
                      >
                        {node.serialized || '(empty)'}
                      </pre>
                    </div>
                  ))
                : null}
            </div>
          </div>
          <p className="tool-live-panel-note">Evaluated locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
